import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { calculateDiscountPrice, formatPrice, handleError } from "../utils/functions";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import axiosInstance from "../config/axios.config";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import TextInput from "./TextInput";
import Select from "./Select";
import Textarea from "./Textarea";
import Loading from "./Refine/Loading";
import SubmitButton from "./SubmitButton";
import useCurrency from "../hooks/useCurrency";
import { useTranslation } from "react-i18next";

const schema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  email: Yup.string().email("Invalid email format").required("Email is required"),
  phoneNumber: Yup.string()
    .matches(/^[0-9]+$/, "Phone number must contain only digits")
    .min(10, "Phone number must be at least 10 digits")
    .required("Phone number is required"),
  countryCode: Yup.string().required("Country is required"),
  stateId: Yup.string().required("State is required"),
  cityId: Yup.string().required("City is required"),
  address: Yup.string().required("Address is required"),
  zipCode: Yup.string().required("Zip Code is required"),
  additional: Yup.string().default(""),
});

const Checkout = () => {
  const [currency] = useCurrency();
  const navigate = useNavigate();
  const { token, user } = useSelector((state) => state.auth);
  const { countryCode } = useSelector((state) => state.config);
  const [isLoading, setIsLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [country, setCountry] = useState([]);

  const [selectedPayment, setSelectedPayment] = useState("COD");
  const [charges, setCharges] = useState({
    estimatedShipment: 0,
    subtotal: 0,
    total: 0,
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({ resolver: yupResolver(schema), mode: "all" });

  const handlePaymentChange = (event) => {
    setSelectedPayment(event.target.id);
  };

  useEffect(() => {
    if (!token) {
      setIsLoading(false);
      return;
    }
    const getData = async () => {
      try {
        setIsLoading(true);
        const { data } = await axiosInstance.get("/cart");
        const { data: res } = await axiosInstance.get("/country/active");
        if (!res.error) {
          setCountry(res.data);
        }
        if (!data.error) {
          setCart(data.data.cart);
          setCharges(data.data.charges);
          reset(user);
        } else {
          toast.error(data.message);
        }
      } catch (error) {
        handleError(error);
      } finally {
        setIsLoading(false);
      }
    };
    getData();
  }, [token, reset, user]);

  const onSubmit = async (values) => {
    try {
      const { data } = await axiosInstance.post("/order/place", {
        ...values,
        paymentMethod: selectedPayment,
      });
      if (!data.error) {
        navigate(selectedPayment !== "COD" ? `/payment?orderId=${data.orderId}` : "/");
        toast.success("Order Placed Successfully");
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const getAcceptedOfferPrice = (offers) => {
    const acceptedOffer = offers?.find((offer) => offer.status === "ACCEPTED");
    const counteredAccepted = offers.find(
      (offer) => offer.status === "COUNTERED" && offer.counterOffer.status === "ACCEPTED"
    );
    return acceptedOffer?.price || counteredAccepted.counterOffer.price || null;
  };

  const { t } = useTranslation();

  return (
    <section className="checkout py-80">
      <div className="container">
        <Loading isLoading={isLoading}>
          <form className="row" onSubmit={handleSubmit(onSubmit)}>
            <div className="col-md-8">
              <div className="bg-white px-20 py-20">
                <span className="title text-xl text-black mb-10 fw-semibold">{t("fillOutInfo")}</span>
                <div className="row gy-3">
                  <div className="col-12">
                    <TextInput
                      name={"name"}
                      register={register}
                      label={t("name")}
                      placeholder={t("enterName")}
                      type={"text"}
                      error={errors.name}
                      isRequired
                    />
                  </div>
                  <div className="col-12">
                    <TextInput
                      name={"email"}
                      error={errors.email}
                      register={register}
                      label={t("email")}
                      placeholder={t("enterEmail")}
                      type={"text"}
                      isRequired
                    />
                  </div>
                  <div className="col-12">
                    <TextInput
                      name={"phoneNumber"}
                      error={errors.phoneNumber}
                      register={register}
                      label={t("phoneNumber")}
                      placeholder={t("enterPhone")}
                      type={"text"}
                      isRequired
                    />
                  </div>
                  <div className="col-12">
                    <Select
                      name={"countryCode"}
                      error={errors.countryCode}
                      register={register}
                      label={t("country")}
                      placeholder={t("selectCountry")}
                      isRequired
                      options={country}
                      defaultValue={countryCode}
                    />
                  </div>
                  <div className="col-sm-12 col-md-6">
                    <TextInput
                      name={"stateId"}
                      error={errors.stateId}
                      register={register}
                      label={t("state")}
                      placeholder={t("selectState")}
                      type={"text"}
                      isRequired
                    />
                  </div>
                  <div className="col-sm-12 col-md-6">
                    <TextInput
                      name={"cityId"}
                      error={errors.cityId}
                      register={register}
                      label={t("city")}
                      placeholder={t("selectCity")}
                      type={"text"}
                      isRequired
                    />
                  </div>
                  <div className="col-12">
                    <TextInput
                      name={"address"}
                      error={errors.address}
                      register={register}
                      label={t("address")}
                      placeholder={t("enterAddress")}
                      type={"text"}
                      isRequired
                    />
                  </div>
                  <div className="col-12">
                    <TextInput
                      name={"zipCode"}
                      error={errors.zipCode}
                      register={register}
                      label={t("zipCode")}
                      placeholder={t("enterZipCode")}
                      type={"text"}
                      isRequired
                    />
                  </div>
                  <div className="col-12">
                    <div className="my-40">
                      <h6 className="text-lg mb-24">{t("additionalInfo")}</h6>
                      <Textarea
                        name={"additional"}
                        register={register}
                        placeholder={t("enterAdditionalInfo")}
                        error={errors.additional}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="checkout-sidebar">
                <div className="bg-white rounded-8 p-24 text-center">
                  <span className="text-gray-900 text-xl fw-semibold">{t("yourOrders")}</span>
                </div>
                <div className="border bg-white border-gray-100 rounded-8 px-24 py-40 mt-24">
                  <div className="mb-32 pb-32 border-bottom border-gray-100 flex-between gap-8">
                    <span className="text-gray-900 fw-medium text-xl font-heading-two">{t("product")}</span>
                    <span className="text-gray-900 fw-medium text-xl font-heading-two">{t("subtotal")}</span>
                  </div>
                  {cart.map((item) => (
                    <div className="flex-between gap-24 mb-32">
                      <div className="flex-align gap-12">
                        <span className="text-gray-900 fw-normal text-md font-heading-two w-144">
                          {item?.product?.name.length > 25
                            ? `${item?.product?.name.slice(0, 25)}...`
                            : item?.product?.name}
                        </span>
                        <span className="text-gray-900 fw-normal text-md font-heading-two">
                          <i className="ph-bold ph-x" />
                        </span>
                        <span className="text-gray-900 fw-semibold text-md font-heading-two">
                          {item.quantity}
                        </span>
                      </div>
                      <span className="text-gray-900 fw-bold text-md font-heading-two">
                        {getAcceptedOfferPrice(item.product.offers)
                          ? formatPrice(getAcceptedOfferPrice(item.product.offers) * item.quantity, currency)
                          : item.product.discount > 0
                          ? formatPrice(
                              calculateDiscountPrice(item.product.price, item.product.discount) *
                                item.quantity,
                              currency
                            )
                          : formatPrice(item.product.price * item.quantity, currency)}
                      </span>
                    </div>
                  ))}
                  <div className="border-top border-gray-100 pt-30  mt-30">
                    <div className="mb-32 flex-between gap-8">
                      <span className="text-gray-700 font-heading-two text-md fw-semibold">
                        {t("subtotal")}
                      </span>
                      <span className="text-gray-900 font-heading-two text-md fw-bold">
                        {formatPrice(charges.subtotal)}
                      </span>
                    </div>
                    <div className="mb-32 flex-between gap-8">
                      <span className="text-gray-700 font-heading-two text-md fw-semibold">
                        {t("estimatedShipment")}
                      </span>
                      <span className="text-gray-900 font-heading-two text-md fw-bold">
                        {formatPrice(charges.estimatedShipment)}
                      </span>
                    </div>
                    <div className="mb-0 flex-between gap-8">
                      <span className="text-gray-700 font-heading-two text-md fw-semibold">{t("total")}</span>
                      <span className="text-gray-900 font-heading-two text-md fw-bold">
                        {formatPrice(charges.total)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-32">
                  <div className="payment-item">
                    <div className="form-check common-check common-radio py-16 mb-0">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="payment"
                        id="ONLINE"
                        checked={selectedPayment === "ONLINE"}
                        onChange={handlePaymentChange}
                      />
                      <label className="form-check-label fw-semibold text-neutral-600" htmlFor="ONLINE">
                        {t("onlinePayment")}
                      </label>
                    </div>
                    {selectedPayment === "ONLINE" && (
                      <div className="payment-item__content px-16 py-24 rounded-8 bg-main-50 position-relative d-block">
                        <p className="text-gray-800">{t("onlinePaymentDesc")}</p>
                      </div>
                    )}
                  </div>
                  <div className="payment-item">
                    <div className="form-check common-check common-radio py-16 mb-0">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="payment"
                        id="COD"
                        checked={selectedPayment === "COD"}
                        onChange={handlePaymentChange}
                      />
                      <label className="form-check-label fw-semibold text-neutral-600" htmlFor="COD">
                        {t("cashOnDelivery")}
                      </label>
                    </div>
                    {selectedPayment === "COD" && (
                      <div className="payment-item__content px-16 py-24 rounded-8 bg-main-50 position-relative d-block">
                        <p className="text-gray-800">{t("cashOnDeliveryDesc")}</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-32 mb-10 pt-12 border-top border-gray-100 ">
                  <span className="text-gray-500 smallText">
                    {t("privacyNotice")}{" "}
                    <Link to="/privacy" className="text-main-600 text-decoration-underline">
                      {t("privacyPolicy")}
                    </Link>
                  </span>
                </div>
                <SubmitButton isSubmitting={isSubmitting} className="w-100">
                  {t("placeOrder")}
                </SubmitButton>
              </div>
            </div>
          </form>
        </Loading>
      </div>
    </section>
  );
};

export default Checkout;
